import React from "react";
import { ChevronRight, MapPin, Plus, Star } from "lucide-react";
import UseContext from "hooks/useContext";
import SectorBadge from "components/sectorBadge/SectorBadge";
import {
    formatHourlyRate,
    formatTemplateCity,
    getTemplateQualification,
    getTemplateTitle,
} from "pages/missionTemplates/utils/templateListing";

/** Small outlined tag of the list (count, "SMIC", "Sous le SMIC"). */
export const ListTag = ({ tone = "neutral", children }) => (
    <span
        className={`inline-flex shrink-0 items-center justify-center rounded border px-2 py-0.5 text-xs font-medium leading-4 ${
            tone === "danger" ? "border-red-200 bg-red-50 text-red-700" : "border-gray-200 bg-gray-50 text-gray-700"
        }`}
    >
        {children}
    </span>
);

/**
 * One job template of the list (Figma "Fiches de poste", node 812:4759): star badge, name
 * and qualification, city, hourly rate and its basis, sector badge, chevron. A template
 * below the minimum wage turns red and asks to be updated.
 */
const TemplateRow = ({ template, onPreview }) => {
    const { language } = UseContext();
    if (!template) return null;

    const label = (key, fallback) => language.getValue(`missionTemplate.list.${key}`, fallback);
    const isBelowMinimumWage = !!template.isBelowMinimumWage;
    const isIndexed = !!template.isIndexedToMinimumWage;
    const hourlyRate = formatHourlyRate(template.salary?.wage_per_hour);
    const qualification = getTemplateQualification(template);
    const city = formatTemplateCity(template.location);

    const openPreview = () => onPreview?.(template);
    const handleKeyDown = (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        // Space would otherwise scroll the page.
        event.preventDefault();
        openPreview();
    };

    return (
        <div
            role="button"
            tabIndex={0}
            data-template-id={template._id}
            onClick={openPreview}
            onKeyDown={handleKeyDown}
            className="flex cursor-pointer items-center gap-4 border-b border-gray-100 bg-white px-5 py-3.5 last:border-b-0 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50"
        >
            <div
                className={`corner-smooth-8 flex h-10 w-10 shrink-0 items-center justify-center ${
                    isBelowMinimumWage ? "bg-red-50" : "bg-gray-100"
                }`}
            >
                <Star size={20} color={isBelowMinimumWage ? "#B91C1C" : "#374151"} aria-hidden="true" />
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="truncate text-sm font-medium leading-5 text-gray-900">{getTemplateTitle(template) || "-"}</p>
                {qualification ? <p className="truncate text-xs leading-4 text-gray-700">{qualification}</p> : null}
            </div>

            <div className="hidden w-[220px] shrink-0 items-center gap-1.5 ls:flex">
                <MapPin size={16} color="#6B7280" aria-hidden="true" />
                <span className="truncate text-xs leading-4 text-gray-700">{city || "-"}</span>
            </div>

            <div className="flex w-[200px] shrink-0 flex-col gap-0.5">
                {hourlyRate ? (
                    <p className="flex items-end gap-0.5 whitespace-nowrap">
                        <span className={`text-sm font-medium leading-5 ${isBelowMinimumWage ? "text-red-700" : "text-gray-900"}`}>
                            {hourlyRate}
                        </span>
                        <span className="text-xs leading-4 text-gray-500">{label("perHour", "/h")}</span>
                    </p>
                ) : null}
                <p className={`text-xs leading-4 ${isBelowMinimumWage ? "text-red-700" : "text-gray-700"}`}>
                    {isBelowMinimumWage
                        ? label("updateTemplate", "Mettez la fiche à jour")
                        : isIndexed
                          ? label("indexedToMinimumWage", "Indexé au SMIC")
                          : label("grossHourlyRate", "Taux horaire brut")}
                </p>
            </div>

            {/* The sector sits where the SMIC tag was: the rate line already says indexed or below. */}
            <div className="flex h-6 w-[160px] shrink-0 items-center justify-end">
                <SectorBadge activity={template.activity} size="sm" className="max-w-full" />
            </div>

            <ChevronRight size={20} color="#374151" className="shrink-0" aria-hidden="true" />
        </div>
    );
};

/** Last line of the list: creates a template, laid out as a template line. */
export const CreateTemplateRow = ({ label, onCreate }) => (
    <button
        type="button"
        onClick={onCreate}
        className="flex w-full items-center gap-4 bg-white px-5 py-3.5 text-left hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50"
    >
        <span className="corner-smooth-8 flex h-10 w-10 shrink-0 items-center justify-center border border-dashed border-gray-300 bg-white">
            <Plus size={20} color="#374151" aria-hidden="true" />
        </span>
        <span className="flex-1 text-sm font-medium leading-5 text-gray-900">{label}</span>
        <ChevronRight size={20} color="#374151" className="shrink-0" aria-hidden="true" />
    </button>
);

export default TemplateRow;
